import axios, { AxiosInstance } from 'axios';
import { ENV } from '../utils/env';
import { auth } from '../config/firebase';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { RetriableRequestConfig, shouldRetryWithFreshToken } from './authRetry';

export abstract class BaseClient {
    protected api: AxiosInstance;

    constructor() {
        this.api = axios.create({
            baseURL: ENV.API_URL(),
            headers: {
                'Content-Type': 'application/json',
            },
        });

        this.initializeInterceptors();
    }

    private initializeInterceptors() {
        // Request Interceptor
        this.api.interceptors.request.use(
            async (config) => {
                // Get fresh token from Firebase, unless the caller passed one
                const user = auth.currentUser;
                if (user && !config.headers.Authorization) {
                    try {
                        const token = await user.getIdToken();
                        config.headers.Authorization = `Bearer ${token}`;
                    } catch (e) {
                        console.error('Failed to get token', e);
                    }
                }

                // Add user ID header (Legacy/Service requirement - TODO REMOVE)
                const userId = localStorage.getItem('user');
                if (userId) {
                    config.headers['x-user-id'] = userId;
                }

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response Interceptor
        this.api.interceptors.response.use(
            (response) => response,
            (error) => this.retryWithFreshToken(error)
        );
    }

    /**
     * A token older than the user's claims (see `authRetry.ts`) gets ONE retry
     * with a force-refreshed token — that is how a session started before the
     * claims existed picks them up. A refresh that fails (a disabled account)
     * or a second refusal goes on as the original error.
     */
    private async retryWithFreshToken(error: unknown): Promise<unknown> {
        const user = auth.currentUser;
        if (user && shouldRetryWithFreshToken(error)) {
            const config = (error as { config: RetriableRequestConfig }).config;
            config.authRetried = true;
            const token = await user.getIdToken(true).catch(() => null);
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
                return this.api.request(config);
            }
        }
        return this.handleResponseError(error);
    }

    /**
     * Put the server's own sentence on `error.message` — axios writes
     * "Request failed with status code 500" there, and every
     * `catch (err) { setError(err.message) }` showed that instead of the
     * reason the service sent. The axios error object is still what is
     * rejected, so `error.response` and `error.code` are unchanged.
     */
    protected handleResponseError(error: any): Promise<any> {
        const serverMessage = apiErrorMessage(error, '');
        if (serverMessage) error.message = serverMessage;
        return Promise.reject(error);
    }
}
